// Iter46 — Standalone "+ New sorting request" modal for the /sorting workspace.
//
// Mirrors the TMS Iter44 pattern: a single modal that lets the user create a
// sorting service request either standalone (no incident link) or linked to
// an existing open incident via a searchable picker. Posts to
// `POST /api/service-requests` (incident_id optional in body).
//
// Auto-opens when the user lands on `/sorting?new=1` from the Service
// Requests hub.
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useStore } from "../store";
import { useBarcodeScanner } from "../lib/useBarcodeScanner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const mono = (size, color, weight, ls) => ({
  fontFamily: "DM Mono, monospace",
  fontSize: size,
  color: color || "var(--text, #f0f2f8)",
  fontWeight: weight || 400,
  letterSpacing: ls,
});

const FieldLabel = ({ children }) => (
  <div className="uppercase mb-1.5" style={mono(9, "var(--dimmer, #9198b0)", 600, "0.8px")}>
    {children}
  </div>
);

const PRIORITIES = ["low", "medium", "high"];

export const NewSortingRequestModal = ({ onClose, onCreated }) => {
  const addToast = useStore((s) => s.addToast);
  const [linkMode, setLinkMode] = useState("standalone");
  const [linkedIncidentId, setLinkedIncidentId] = useState("");
  const [incidentSearch, setIncidentSearch] = useState("");
  const [openIncidents, setOpenIncidents] = useState([]);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("medium");
  // Iter52 — Part identification + sorting scope.
  const [partNumber, setPartNumber] = useState("");
  const [partDescription, setPartDescription] = useState("");
  const [scopeType, setScopeType] = useState("quantity");
  const [targetQuantity, setTargetQuantity] = useState("");
  const [untilDatetime, setUntilDatetime] = useState("");
  const scanner = useBarcodeScanner({
    onScanned: (code) => {
      setPartNumber(code);
      addToast({ type: "success", title: "Barcode scanned", body: code });
    },
    onError: (msg) => setErr(msg),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/incidents?status_in=open,investigating,escalated&limit=200`)
      .then((r) => { if (!cancelled) setOpenIncidents(r.data || []); })
      .catch(() => { /* swallow */ });
    return () => { cancelled = true; };
  }, []);

  const filteredIncidents = useMemo(() => {
    const q = incidentSearch.trim().toLowerCase();
    if (!q) return openIncidents.slice(0, 25);
    return openIncidents
      .filter((i) => (i.id + " " + (i.title || "")).toLowerCase().includes(q))
      .slice(0, 25);
  }, [openIncidents, incidentSearch]);


  const submit = async () => {
    setErr(null);
    if (!title.trim()) { setErr("Title is required."); return; }
    if (linkMode === "incident" && !linkedIncidentId) {
      setErr("Pick an incident or switch to standalone.");
      return;
    }
    // Iter52 — scope-specific validation.
    let sortingScope = null;
    if (scopeType === "quantity") {
      const qty = parseInt(targetQuantity, 10);
      if (!qty || qty <= 0) { setErr("Enter a positive target quantity."); return; }
      sortingScope = { type: "quantity", target_quantity: qty };
    } else if (scopeType === "until_date") {
      if (!untilDatetime) { setErr("Pick a target date/time."); return; }
      sortingScope = { type: "until_date", until_datetime: new Date(untilDatetime).toISOString() };
    } else if (scopeType === "clean_point") {
      sortingScope = { type: "clean_point", clean_point_plus_one_verified: false };
    }
    setBusy(true);
    try {
      const body = {
        type: "sorting",
        title: title.trim(),
        description: description.trim() || null,
        priority,
        part_number: partNumber.trim() || null,
        part_description: partDescription.trim() || null,
        sorting_scope: sortingScope,
      };
      if (linkMode === "incident") body.incident_id = linkedIncidentId;
      const r = await axios.post(`${API}/service-requests`, body);
      addToast({ type: "success", title: "Sorting request created", body: r.data?.id || "" });
      onCreated(r.data);
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setErr(String(detail));
      addToast({ type: "error", title: "Failed to create sorting request", body: String(detail) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.65)",
        zIndex: 500,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
      data-testid="sorting-create-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.12)",
          borderRadius: 8,
          width: "min(640px, 96vw)",
          maxHeight: "92vh",
          overflow: "auto",
          boxShadow: "0 18px 60px rgba(0,0,0,0.6)",
        }}
      >
        <div className="px-5 py-4 flex items-center justify-between"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div>
            <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 800, fontSize: 18, color: "var(--text, #f0f2f8)" }}>
              New sorting request
            </div>
            <div className="mt-0.5" style={mono(10, "var(--dimmer, #9198b0)")}>
              Standalone or linked to an existing incident
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "transparent", border: "none", color: "var(--dimmer, #9198b0)", fontSize: 22, cursor: "pointer" }}
            data-testid="sorting-create-close"
          >×</button>
        </div>

        <div className="px-5 py-4 space-y-5">
          {/* Link-to-incident toggle */}
          <div data-testid="sorting-incident-link-section">
            <FieldLabel>Link to incident</FieldLabel>
            <div className="flex gap-2 mb-2 flex-wrap">
              {[
                { v: "standalone", label: "Standalone (no incident)" },
                { v: "incident", label: "Link to incident" },
              ].map((m) => (
                <button
                  type="button"
                  key={m.v}
                  onClick={() => setLinkMode(m.v)}
                  className="px-3 py-1.5 rounded"
                  style={{
                    background: linkMode === m.v ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${linkMode === m.v ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                    color: linkMode === m.v ? "#9ec3ec" : "var(--text, #c5cad8)",
                    ...mono(10, undefined, 700, "0.4px"),
                    cursor: "pointer",
                  }}
                  data-testid={`sorting-link-mode-${m.v}`}
                >{m.label}</button>
              ))}
            </div>
            {linkMode === "incident" && (
              <>
                <input
                  type="search"
                  placeholder="Search incidents by id or title…"
                  value={incidentSearch}
                  onChange={(e) => setIncidentSearch(e.target.value)}
                  className="w-full px-2 py-1.5 rounded outline-none mb-1.5"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
                  data-testid="sorting-incident-search"
                />
                <div
                  className="rounded-md max-h-40 overflow-y-auto"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)" }}
                  data-testid="sorting-incident-picker-list"
                >
                  {filteredIncidents.length === 0 ? (
                    <div className="px-3 py-2" style={mono(11, "var(--dimmer, #9198b0)")}>No open incidents matched.</div>
                  ) : filteredIncidents.map((i) => (
                    <label
                      key={i.id}
                      className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                      style={{
                        borderBottom: "1px solid rgba(255,255,255,0.04)",
                        background: linkedIncidentId === i.id ? "rgba(24,95,165,0.10)" : "transparent",
                      }}
                    >
                      <input
                        type="radio"
                        name="sorting-link-incident"
                        checked={linkedIncidentId === i.id}
                        onChange={() => setLinkedIncidentId(i.id)}
                        data-testid={`sorting-pick-incident-${i.id}`}
                      />
                      <span style={mono(11, "var(--text, #f0f2f8)", 600)}>{i.id}</span>
                      <span style={mono(10, "var(--dimmer, #9198b0)")} className="truncate">{i.title || ""}</span>
                    </label>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Title + Description + Priority */}
          <div>
            <FieldLabel>Title *</FieldLabel>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Sort 300 wiring harnesses on dock 12"
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
              data-testid="sorting-create-title"
            />
          </div>

          {/* Iter52 — Part identification */}
          <div data-testid="sorting-part-section">
            <FieldLabel>Part number / SKU</FieldLabel>
            <div className="flex gap-1.5">
              <input
                type="text"
                value={partNumber}
                onChange={(e) => setPartNumber(e.target.value)}
                placeholder="e.g. 51 41 7 234 091"
                className="flex-1 px-2 py-1.5 rounded outline-none"
                style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                data-testid="sorting-create-part-number"
              />
              {scanner.supported && (
                <button
                  type="button"
                  onClick={scanner.scan}
                  disabled={scanner.busy}
                  className="px-2.5 py-1.5 rounded"
                  style={{ background: "rgba(78,208,132,0.10)", border: "1px solid rgba(78,208,132,0.40)", color: "#9aebc2", ...mono(10, undefined, 700, "0.4px"), cursor: scanner.busy ? "wait" : "pointer", whiteSpace: "nowrap" }}
                  data-testid="sorting-create-part-scan"
                  title="Scan barcode using device camera"
                >
                  {scanner.busy ? "📷 Scanning…" : "📷 Scan"}
                </button>
              )}
            </div>
            <div className="mt-2">
              <FieldLabel>Part description / name</FieldLabel>
              <input
                type="text"
                value={partDescription}
                onChange={(e) => setPartDescription(e.target.value)}
                placeholder="e.g. LH wiring harness, A-pillar"
                className="w-full px-2 py-1.5 rounded outline-none"
                style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11) }}
                data-testid="sorting-create-part-description"
              />
            </div>
          </div>

          {/* Iter52 — Sorting scope */}
          <div data-testid="sorting-scope-section">
            <FieldLabel>Sorting scope * — how do we know when this is done?</FieldLabel>
            <div className="space-y-1.5">
              {[
                { v: "quantity", label: "Fixed quantity", desc: "Sort a specific number of parts" },
                { v: "until_date", label: "Until date/time", desc: "Sort everything until the given moment" },
                { v: "clean_point", label: "Until clean point", desc: "Sort until supplier confirms clean point DN — and verify DN+1" },
              ].map((s) => (
                <label
                  key={s.v}
                  className="flex items-start gap-2 px-2 py-1.5 rounded cursor-pointer"
                  style={{
                    background: scopeType === s.v ? "rgba(24,95,165,0.10)" : "transparent",
                    border: `1px solid ${scopeType === s.v ? "rgba(24,95,165,0.45)" : "rgba(255,255,255,0.10)"}`,
                  }}
                  data-testid={`sorting-create-scope-${s.v}`}
                >
                  <input
                    type="radio"
                    name="sorting-scope"
                    checked={scopeType === s.v}
                    onChange={() => setScopeType(s.v)}
                    className="mt-1"
                    data-testid={`sorting-create-scope-radio-${s.v}`}
                  />
                  <div style={{ minWidth: 0 }}>
                    <div style={mono(11, "var(--text, #f0f2f8)", 700, "0.3px")}>{s.label}</div>
                    <div style={mono(10, "var(--dimmer, #9198b0)")}>{s.desc}</div>
                  </div>
                </label>
              ))}
            </div>

            {scopeType === "quantity" && (
              <div className="mt-2.5" data-testid="sorting-scope-quantity-input">
                <FieldLabel>Target quantity (parts)</FieldLabel>
                <input
                  type="number"
                  min="1"
                  value={targetQuantity}
                  onChange={(e) => setTargetQuantity(e.target.value)}
                  placeholder="e.g. 5000"
                  className="w-full px-2 py-1.5 rounded outline-none"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                  data-testid="sorting-create-target-quantity"
                />
              </div>
            )}
            {scopeType === "until_date" && (
              <div className="mt-2.5" data-testid="sorting-scope-until-date-input">
                <FieldLabel>Until date / time</FieldLabel>
                <input
                  type="datetime-local"
                  value={untilDatetime}
                  onChange={(e) => setUntilDatetime(e.target.value)}
                  className="w-full px-2 py-1.5 rounded outline-none"
                  style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(12) }}
                  data-testid="sorting-create-until-datetime"
                />
              </div>
            )}
            {scopeType === "clean_point" && (
              <div
                className="mt-2.5 px-3 py-2 rounded"
                style={{ background: "rgba(243,177,74,0.08)", border: "1px dashed rgba(243,177,74,0.35)", ...mono(10, "#f3b14a") }}
                data-testid="sorting-scope-clean-point-hint"
              >
                <strong>Safety rule:</strong> after the clean-point DN, ONE more inbound delivery (DN+1) must be inspected before the sorting request can close. Register the clean-point DN later from the task card.
              </div>
            )}
          </div>

          <div>
            <FieldLabel>Description</FieldLabel>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              placeholder="Optional context — part number, defect signature, target rate..."
              className="w-full px-2 py-1.5 rounded outline-none"
              style={{ background: "var(--bg-base, #08090d)", border: "1px solid rgba(255,255,255,0.10)", color: "var(--text, #f0f2f8)", ...mono(11), resize: "vertical" }}
              data-testid="sorting-create-description"
            />
          </div>
          <div>
            <FieldLabel>Priority</FieldLabel>
            <div className="flex flex-wrap gap-1.5">
              {PRIORITIES.map((p) => (
                <button
                  type="button"
                  key={p}
                  onClick={() => setPriority(p)}
                  className="px-2.5 py-1 rounded"
                  style={{
                    background: priority === p ? "rgba(24,95,165,0.18)" : "transparent",
                    border: `1px solid ${priority === p ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                    color: priority === p ? "#9ec3ec" : "var(--text, #c5cad8)",
                    ...mono(10, undefined, 700, "0.4px"),
                    textTransform: "uppercase",
                    cursor: "pointer",
                  }}
                  data-testid={`sorting-create-priority-${p}`}
                >{p}</button>
              ))}
            </div>
          </div>

          {err && (
            <div
              className="rounded px-3 py-2"
              style={{ background: "rgba(226,75,74,0.12)", border: "1px solid rgba(226,75,74,0.4)", color: "#ff9a99", ...mono(11) }}
              data-testid="sorting-create-error"
            >{err}</div>
          )}
        </div>

        <div className="px-5 py-3 flex justify-end gap-2"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 rounded"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "var(--text, #c5cad8)", ...mono(10, undefined, 600, "0.4px"), cursor: "pointer" }}
            data-testid="sorting-create-cancel"
          >Cancel</button>
          <button
            type="button"
            onClick={submit}
            disabled={busy}
            className="px-3 py-1.5 rounded"
            style={{
              background: "#185FA5",
              color: "#fff",
              ...mono(10, undefined, 700, "0.4px"),
              border: "none",
              cursor: busy ? "wait" : "pointer",
              opacity: busy ? 0.6 : 1,
            }}
            data-testid="sorting-create-submit"
          >{busy ? "Creating…" : "Create request"}</button>
        </div>
      </div>
    </div>
  );
};
